import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Stack, Card, Title, Text, Group, Button, Badge, Skeleton } from '@repletoryreach/sdk-ui';
import { Alert, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { email } from '@repletoryreach/sdk-api';
import { formatDateTime, formatInteger, formatRelative } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';

const EmailIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="m4 7 8 6 8-6" />
  </svg>
);

/** The email-routing enum codes, as the routing row stores them, mapped to the provider rows that hold their keys. */
const ROW_OF: Record<string, string> = { ses: 'aws_ses', sendgrid: 'sendgrid', mailgun: 'mailgun', postmark: 'postmark' };

/**
 * Customer email at a glance (4 October 2026): whether it can be sent at all,
 * which provider sends, which one stands in and which one receives, what the
 * last day's sends came to, when a provider last called back and how many
 * addresses are suppressed. The provider rows below hold the keys; this card
 * says whether the whole path works, and sends one email to prove it.
 *
 * `nameOf` turns a provider row code into the name on its card.
 */
export function EmailCard({ nameOf }: { nameOf: (code: string) => string }) {
  const t = useT();
  const [to, setTo] = useState('');
  const [toError, setToError] = useState('');

  const statusQ = useQuery({
    queryKey: ['admin', 'email-status'],
    queryFn: () => email.adminStatus(),
    retry: false
  });
  // As SmsCard: anything that is not the status object reads as no status, never a blank page.
  const raw = statusQ.data?.data;
  const status = raw && !Array.isArray(raw) && typeof raw === 'object' ? { ...raw, last_24_hours: raw.last_24_hours ?? [] } : null;

  const testM = useMutation({
    mutationFn: (address: string) => email.adminTest({ to: address }),
    onSettled: () => { void statusQ.refetch(); }
  });
  const result = testM.data?.data ?? null;

  const provider = (code?: string | null) => (code ? nameOf(ROW_OF[code] ?? code) : t('settings.integrations.email.none'));
  const statusLabel = (code: string) => t(`settings.integrations.email.status.${code}`, code);

  function sendTest() {
    const address = to.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) {
      setToError(t('settings.integrations.email.test.invalid'));
      return;
    }
    openConfirmModal({
      title: t('settings.integrations.email.test.confirmTitle'),
      children: (
        <p style={{ margin: 0 }}>
          {t('settings.integrations.email.test.confirmBody', { address, provider: provider(status?.active_provider) })}
        </p>
      ),
      labels: { confirm: t('settings.integrations.email.test.send'), cancel: t('action.cancel') },
      onConfirm: () => testM.mutate(address)
    });
  }

  const resultOk = result && (result.status === 'queued' || result.status === 'sent' || result.status === 'delivered');

  return (
    <Card withBorder data-testid="admin-email-card">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="sm" align="flex-start" wrap="nowrap" style={{ minWidth: 0 }}>
            <span className="rr-integrations__tile" aria-hidden>{EmailIcon}</span>
            <Stack gap="xs" style={{ minWidth: 0 }}>
              <Title order={5}>{t('settings.integrations.email.title')}</Title>
              <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.integrations.email.subtitle')}</Text>
            </Stack>
          </Group>
          {status && (
            <StatusBadge status={status.available ? 'healthy' : 'warning'}>
              {status.available ? t('settings.integrations.email.available') : t('settings.integrations.email.unavailable')}
            </StatusBadge>
          )}
        </Group>

        {statusQ.isPending && <Skeleton height={96} />}

        {statusQ.error && (
          <Alert color="error">
            <Group justify="space-between" align="center" wrap="wrap">
              <Text role="bodySmall">{t('settings.integrations.email.loadError')}</Text>
              <Button size="xs" variant="outline" onClick={() => { void statusQ.refetch(); }}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        )}

        {status && (
          <Stack gap="sm">
            {!status.available && (
              <Alert color="warning">{status.reason || t('settings.integrations.email.unavailableHint')}</Alert>
            )}

            <Row label={t('settings.integrations.email.active')}>{provider(status.active_provider)}</Row>
            <Row label={t('settings.integrations.email.backup')}>{provider(status.backup_provider)}</Row>
            <Row label={t('settings.integrations.email.inbound')}>{provider(status.inbound_provider)}</Row>
            <Row label={t('settings.integrations.email.domain')}>{status.sending_domain}</Row>
            <Row label={t('settings.integrations.email.last24')}>
              {status.last_24_hours.length === 0
                ? t('settings.integrations.email.noneSent')
                : (
                  <Group gap={4} wrap="wrap">
                    {status.last_24_hours.map((s) => (
                      <Badge
                        key={s.status}
                        size="xs"
                        variant="light"
                        color={s.status === 'failed' || s.status === 'refused' || s.status === 'bounced' || s.status === 'complained' ? 'red' : s.status === 'delivered' ? 'green' : 'gray'}
                      >
                        {t('settings.integrations.email.statusCount', { status: statusLabel(s.status), count: formatInteger(s.count) })}
                      </Badge>
                    ))}
                  </Group>
                )}
            </Row>
            <Row label={t('settings.integrations.email.lastWebhook')}>
              {status.last_webhook
                ? (
                  <span title={formatDateTime(status.last_webhook.received_at)}>
                    {t(status.last_webhook.outcome ? 'settings.integrations.email.webhookWithOutcome' : 'settings.integrations.email.webhook', {
                      provider: provider(status.last_webhook.provider_code),
                      when: formatRelative(status.last_webhook.received_at),
                      outcome: status.last_webhook.outcome ?? ''
                    })}
                  </span>
                )
                : t('settings.integrations.email.noWebhook')}
            </Row>
            <Row label={t('settings.integrations.email.suppressions')}>
              {t('settings.integrations.email.suppressionCount', {
                count: formatInteger(status.suppressions),
                platform: formatInteger(status.platform_wide_suppressions)
              })}
            </Row>
          </Stack>
        )}

        <Group gap="sm" align="flex-start" wrap="wrap" className="rr-integrations__foot">
          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
            <TextInput
              label={t('settings.integrations.email.test.address')}
              description={t('settings.integrations.email.test.addressHint')}
              placeholder="you@example.co.za"
              value={to}
              type="email"
              error={toError || undefined}
              onChange={(e) => { setTo(e.currentTarget.value); setToError(''); }}
            />
          </div>
          <Button
            size="sm"
            style={{ marginTop: 26 }}
            loading={testM.isPending}
            disabled={!status?.available || !to.trim()}
            onClick={sendTest}
          >
            {t('settings.integrations.email.test.send')}
          </Button>
        </Group>

        {testM.error && (
          <Alert color="error">
            {testM.error instanceof Error ? testM.error.message : t('common.unknownError')}
          </Alert>
        )}
        {result && (
          <Alert color={resultOk ? 'success' : 'error'}>
            <Stack gap={2}>
              <Text role="bodySmall">{t('settings.integrations.email.test.result', { status: statusLabel(result.status) })}</Text>
              {(result.error_code || result.error) && (
                <Text role="caption">{[result.error_code, result.error].filter(Boolean).join(': ')}</Text>
              )}
            </Stack>
          </Alert>
        )}
      </Stack>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Group gap="sm" align="flex-start" wrap="nowrap">
      <Text role="caption" c="var(--rr-color-text-muted)" style={{ minWidth: 150 }}>{label}</Text>
      <Text role="bodySmall" as="div" style={{ minWidth: 0 }}>{children}</Text>
    </Group>
  );
}
